//@flow
import React, {useState} from 'react';
import {makeStyles, Typography, Collapse, Hidden} from '@material-ui/core';
import {KeyboardArrowDown, Subject, KeyboardArrowUp} from '@material-ui/icons';

import CustomButton from '../../../../elements/CustomButton/CustomButton';

import {getImageUrl} from '../../../../utils/image_provider';
import {commaFormat} from '../../../../utils/number';
import {colors} from '../../../../assets/styles/colors';

import type {
  SinglePromotion,
  Currency,
  ShoppingCartItem,
} from '../../../../entities';

type TermsAndConditionsProps = {
  terms: string,
};

type PromotionItemProps = {
  promotion: SinglePromotion,
  currency: Currency,
  productsInShoppingCart: ShoppingCartItem[],
  terms: string,
  description?: any,
  percentageProgress: number | null,
  onViewProducts: (promotionId: string) => void,
  promotionScope: 'PRODUCT' | 'SHIPPING',
};

const useStyle = makeStyles(theme => ({
  item: {
    padding: '18px',
    borderRadius: '12px',
    background: colors.grey200,
    width: '100%',
    paddingBottom: '.25rem',
    margin: '8px 0',
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  productCounter: {
    fontSize: '12px',
    padding: '2px 8px',
    borderRadius: '4px',
    background: 'rgba(212, 212, 221, 0.61)',
    color: colors.grey700,
    fontWeight: '600',
    marginBottom: '14px',
    display: 'inline-block',
  },
  products: {
    display: 'flex',
    flexWrap: 'nowrap',
    width: '100%',
    gap: '4px',
    overflow: 'auto',
  },
  productItem: {
    padding: '8px',
    display: 'flex',
    background: 'white',
    borderRadius: '4px',
    flexGrow: 1,
    gap: '16px',
    minWidth: '242px',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  productData: {
    display: 'flex',
    flexGrow: 1,
    flexWrap: 'wrap',
  },
  productImg: {
    width: '42px',
    height: '42px',
    borderRadius: '4px',
  },
  productName: {
    flexGrow: 1,
    fontSize: '14px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  productPrice: {
    fontSize: '14px',
    fontWeight: 500,
  },
  promotionDescription: {
    fontSize: '14px',
    fontWeight: 500,
    color: colors.grey800,
  },
  promotionDetails: {
    display: 'flex',
    alignItems: 'center',
    margin: '8px 0',
    gap: '8px',
    justifyContent: 'space-between',
  },
  terms: {
    margin: '8px 0',
  },
  termsTitle: {
    fontSize: '12px',
    color: colors.grey700,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1px',
    cursor: 'pointer',
  },
  termsText: {
    cursor: 'pointer',
    fontSize: '12px',
    color: colors.grey600,
  },
  smallIcon: {
    fontSize: '14px',
  },
  containerLine: {
    width: '100%',
    background: colors.grey300,
    height: '4px',
    borderRadius: '4px',
  },
  line: {
    height: '4px',
    borderRadius: '4px',
    background: colors.purple,
    maxWidth: '100%',
  },
  mobileBtn: {
    paddingInline: '40px',
    margin: '0 auto',
    display: 'block',
  },
}));

function TermsAndConditions({terms}: TermsAndConditionsProps) {
  const classes = useStyle();

  const [open, setOpen] = useState(false);

  return (
    <div className={classes.terms}>
      {!open && (
        <Typography
          className={classes.termsTitle}
          role="button"
          onClick={() => setOpen(true)}
        >
          <KeyboardArrowDown />
          <Subject className={classes.smallIcon} />
          Terminos y condiciones. Ver más.
        </Typography>
      )}

      <Collapse in={open}>
        <Typography
          className={classes.termsText}
          role="button"
          onClick={() => setOpen(false)}
        >
          <KeyboardArrowUp className={classes.smallIcon} />
          <span style={{color: colors.grey700}}>Terminos y condiciones </span>
          {terms}
        </Typography>
      </Collapse>
    </div>
  );
}

export default function PromotionItem(props: PromotionItemProps) {
  const {
    currency,
    productsInShoppingCart,
    terms,
    description,
    percentageProgress,
    promotion,
    onViewProducts,
    promotionScope,
  } = props;
  const classes = useStyle();

  const totalProducts = productsInShoppingCart.reduce((acc, product) => {
    return acc + product.amount;
  }, 0);

  const handleSeeProducts = () => {
    if (onViewProducts) {
      onViewProducts(promotion.id);
    }
  };

  return (
    <div className={classes.item}>
      <div className={classes.itemHeader}>
        {promotionScope === 'PRODUCT' && (
          <span className={classes.productCounter}>
            {totalProducts} {totalProducts === 1 ? ' producto' : ' productos'}{' '}
            en el carrito
          </span>
        )}
        <span className={classes.productCounter}>{promotion.name}</span>
      </div>
      <div className={classes.products}>
        {productsInShoppingCart.map(product => (
          <div className={classes.productItem}>
            <img
              className={classes.productImg}
              src={getImageUrl(product.product.mainImageId)}
              alt=""
            />
            <div className={classes.productData}>
              <Typography className={classes.productName}>
                {product.product.name}
              </Typography>
              <Typography className={classes.productPrice}>
                {commaFormat(product.product.price, currency.symbol)}
              </Typography>
            </div>
          </div>
        ))}
      </div>
      <div className={classes.promotionDetails}>
        <Typography className={classes.promotionDescription}>
          {description}
        </Typography>
        <Hidden smDown>
          <CustomButton onClick={handleSeeProducts} variant="contained">
            Ver productos
          </CustomButton>
        </Hidden>
      </div>
      {percentageProgress !== null && (
        <div className={classes.containerLine}>
          <div
            className={classes.line}
            style={{
              width: `${percentageProgress || 0}%`,
            }}
          />
        </div>
      )}

      <TermsAndConditions terms={terms} />
      <Hidden smUp>
        <CustomButton
          onClick={handleSeeProducts}
          className={classes.mobileBtn}
          variant="contained"
        >
          Ver productos
        </CustomButton>
      </Hidden>
    </div>
  );
}
